import { empty, BehaviorSubject } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { SettingsManager } from '@shared/app/scripts/services/settings-manager';
import { PayNl } from '@shared/app/scripts/services/pay-nl';

/**
 * @ngdoc service
 * @name posApp.pinProvider
 * @description
 * Proxy service, redirecting everything to {@link posApp.pinProvider payPlaza}
 * or {@link https://docs.dev.swcloud.nl/softwear7/injectables/PayNl.html payNl}
 * depending on the settings value
 */
angular
  .module('posApp')
  .factory(
    'pinProvider',
    function (settingsManager: SettingsManager, payPlaza: any, payNl: PayNl) {
      var pinProvider = {};

      var pinImplementation = undefined,
        pinImplementation$ = new BehaviorSubject(payPlaza);

      pinImplementation$.subscribe(function (implementation) {
        pinImplementation = implementation;
      });

      /**
       * @ngdoc
       * @name posApp.pinProvider#showTransactionMethods$
       * @propertyOf posApp.pinProvider
       * @description
       * rxjs's observable; emits string each time there is a showTransactionMethods event
       * out form pay plaza
       */
      pinProvider.showTransactionMethods$ = pinImplementation$.pipe(
        switchMap(function (implementation) {
          return implementation.showTransactionMethods$;
        }),
      );

      /**
       * @ngdoc
       * @name posApp.pinProvider#retryMessage$
       * @propertyOf posApp.pinProvider
       * @description
       * rxjs's observable; emits string each time there is a retryMessage event
       * out form pay plaza
       */
      pinProvider.retryMessage$ = pinImplementation$.pipe(
        switchMap(function (implementation) {
          return implementation.retryMessage$
            ? implementation.retryMessage$
            : empty();
        }),
      );

      /**
       * @ngdoc
       * @name posApp.pinProvider#errorMessage$
       * @propertyOf posApp.pinProvider
       * @description
       * rxjs's observable; that emits message string each time there is a errorMessage event
       * out form pay plaza
       */
      pinProvider.errorMessage$ = pinImplementation$.pipe(
        switchMap(function (implementation) {
          return implementation.errorMessage$
            ? implementation.errorMessage$
            : empty();
        }),
      );

      /**
       * @ngdoc
       * @name posApp.pinProvider#transactionId$
       * @propertyOf posApp.pinProvider
       * @description
       * rxjs's observable; that emits transactionId value every time when we receive
       * it form for pay plaza
       */
      pinProvider.transactionId$ = pinImplementation$.pipe(
        switchMap(function (implementation) {
          return implementation.transactionId$;
        }),
      );

      /**
       * @ngdoc
       * @name posApp.pinProvider#inProgress$
       * @propertyOf posApp.pinProvider
       * @description
       * rxjs's observable; that emits true when the transaction is updated to be
       * in progress, and false when it's terminated
       */
      pinProvider.inProgress$ = pinImplementation$.pipe(
        switchMap(function (implementation) {
          return implementation.inProgress$;
        }),
      );

      /**
       * @ngdoc
       * @name posApp.pinProvider#print$
       * @propertyOf posApp.pinProvider
       * @description
       * rxjs's observable, emits printable content, every time when when we get
       * printing order form pay plaza
       */
      pinProvider.print$ = pinImplementation$.pipe(
        switchMap(function (implementation) {
          return implementation.print$;
        }),
      );

      /**
       * @ngdoc
       * @name posApp.pinProvider#update$
       * @propertyOf posApp.pinProvider
       * @description
       * stream that emits string every time there is an updated from pay plaza
       */
      pinProvider.update$ = pinImplementation$.pipe(
        switchMap(function (implementation) {
          return implementation.update$;
        }),
      );

      /**
       * @ngdoc
       * @name posApp.pinProvider#appName$
       * @propertyOf posApp.pinProvider
       * @description
       * stream that emits string every time we learn an app name
       */
      pinProvider.appName$ = pinImplementation$.pipe(
        switchMap(function (implementation) {
          return implementation.appName$;
        }),
      );

      /**
       * @ngdoc
       * @name posApp.pinProvider#hasCcv$
       * @propertyOf posApp.pinProvider
       * @description
       * rxjs's observable; that emits true when the status URL contains 'ccv'
       */
      pinProvider.hasCcv$ = pinImplementation$.pipe(
        switchMap(function (implementation) {
          return implementation.hasCcv$ ? implementation.hasCcv$ : empty();
        }),
      );

      /**
       * @ngdoc
       * @name posApp.pinProvider#init
       * @methodOf posApp.pinProvider
       * @description
       * Checks if PIN is configured correctly; displays error otherwise
       */
      pinProvider.init = function () {
        if (settingsManager.settings.pinProvider === 'payPlaza4') {
          pinImplementation$.next(payPlaza);
          payPlaza.init();
        } else if (
          settingsManager.settings.pinProvider === 'loyyo' ||
          settingsManager.settings.pinProvider === 'payNL'
        ) {
          pinImplementation$.next(payNl);
        } else {
          payPlaza.init();
        }
      };

      /**
       * @ngdoc
       * @name posApp.pinProvider#startTransaction
       * @methodOf posApp.pinProvider
       * @description
       * Initiates a transaction with PayPlaza web service
       * @param {object} transaction
       * transaction we will work on
       * @return {promise} promise
       * Return promise, that is resolved with the print receipt when transaction is
       * accepted.
       */
      pinProvider.startTransaction = function (transaction, amount) {
        // TODO cover return by test
        return pinImplementation.startTransaction.apply(
          pinImplementation,
          arguments,
        );
      };

      /**
       * @ngdoc
       * @name posApp.pinProvider#setTransactionMethod
       * @methodOf posApp.pinProvider
       * @deprecated
       * @description
       * dummy method, has no meaning in pay plaza v4
       * Method to sends chosen application to pay plaza
       */
      pinProvider.setTransactionMethod = function () {
        pinImplementation.setTransactionMethod.apply(
          pinImplementation,
          arguments,
        );
      };

      /**
       * @ngdoc
       * @name posApp.pinProvider#checkServer
       * @methodOf posApp.pinProvider
       * @description
       * Checks server for any outstanding transactions that should be completed
       * @param {object} transaction
       * the transaction that we will continue if there is still some pending
       * messages
       * @returns {Promise}
       * Return promise, that resolves if there pending transaction & rejects otherwise.
       * It resolves with object that contains pickedUpTransactionPromise - a promise that
       * resolves with print receipt when pass or reject with error receipts.
       */
      pinProvider.checkServer = function (transaction) {
        // TODO cover return by test
        return pinImplementation.checkServer.apply(
          pinImplementation,
          arguments,
        );
      };

      /**
       * @ngdoc
       * @name posApp.pinProvider#abortTransaction
       * @methodOf posApp.pinProvider
       * @description
       * Stop the current transaction
       */
      pinProvider.abortTransaction = function () {
        pinImplementation.abortTransaction.apply(pinImplementation, arguments);
      };

      /**
       * @ngdoc
       * @name posApp.pinProvider#reprintPINReceipt
       * @methodOf posApp.pinProvider
       * @description
       * Reprint a PIN receipt SWUI-978
       * The caveat with re-printing is a connection timeout is expected. We send
       * the request to reprint on the timeout. It's a very strange workflow.
       */
      pinProvider.reprintPINReceipt = function () {
        pinImplementation.reprintPINReceipt.apply(pinImplementation, arguments);
      };

      return pinProvider;
    },
  );
